import { useCallback, useRef, useState } from "react";
import type { TReviewLineItem } from "./types";
import type { TApiLineItemQuery } from "@/apis/proda_finance/types";

type TUseAutoPopulateDeps = {
    lineItems: TReviewLineItem[];
    filteredLineItems: TReviewLineItem[] | null;
    /** Server-side count of rows matching the active filter (whole dataset). */
    matchingRowCount: number;
    /** Active column filter mapped to API query params (no skip/limit). */
    activeFilterQuery: TApiLineItemQuery;
    onAutoPopulatePropcodes: (propcode: string, lineItemIds: string[]) => Promise<number>;
    onAutoPopulateSegments: (
        segmentType: number,
        value: string,
        updateOnlyRequired: boolean,
        lineItemIds: string[],
    ) => Promise<number>;
    onAutoPopulatePropcodesByFilter: (
        filterQuery: TApiLineItemQuery,
        propcode: string,
    ) => Promise<number>;
    onAutoPopulateSegmentsByFilter: (
        filterQuery: TApiLineItemQuery,
        segmentType: number,
        value: string,
        updateOnlyRequired: boolean,
    ) => Promise<number>;
    /** Server-side master property-code search: called with the typed query string. */
    searchMasterPropertyCodes: (query: string) => Promise<Array<{ value: string; label: string }>>;
    /** Lazy segment-value search: called with the chosen segment type and query string. */
    searchSegmentValues: (
        segmentType: number,
        query: string,
    ) => Promise<Array<{ value: string; label: string }>>;
    onRefresh: () => void;
    /** Surface a success message (e.g. a toast/banner) once an auto-populate completes. */
    onSuccess?: (message: string) => void;
};

/**
 * Encapsulates the propcode and segment auto-populate modals: open/close state,
 * lookup-option loading, and the submit handlers. Extracted from ViewFileReview.
 */
export function useAutoPopulate({
    lineItems: _lineItems,
    filteredLineItems: _filteredLineItems,
    matchingRowCount,
    activeFilterQuery,
    onAutoPopulatePropcodes: _onAutoPopulatePropcodes,
    onAutoPopulateSegments: _onAutoPopulateSegments,
    onAutoPopulatePropcodesByFilter,
    onAutoPopulateSegmentsByFilter,
    searchMasterPropertyCodes,
    searchSegmentValues,
    onRefresh,
    onSuccess,
}: TUseAutoPopulateDeps) {
    const [isAutoPopulateOpen, setIsAutoPopulateOpen] = useState(false);
    // Full selected option (label "code - description") for the propcode SearchableSelect.
    const [selectedPropcodeOption, setSelectedPropcodeOption] = useState<{
        value: string;
        label: string;
    } | null>(null);
    const selectedPropcode = selectedPropcodeOption?.value ?? "";
    const [autoPopulateError, setAutoPopulateError] = useState<string | null>(null);
    const [isAutoPopulating, setIsAutoPopulating] = useState(false);
    const [isSegmentPopulateOpen, setIsSegmentPopulateOpen] = useState(false);
    const [selectedSegmentType, setSelectedSegmentType] = useState<number>(1);
    const [segmentValue, setSegmentValue] = useState("");
    // Full selected option (label "code - description") for the segment-value SearchableSelect.
    const [segmentValueOption, setSegmentValueOption] = useState<{
        value: string;
        label: string;
    } | null>(null);
    // Ticked by default: a line is only ever invalid for a missing segment when that
    // segment is mandatory for its account, so the mandatory-only set is exactly the
    // set blocking the file. Untick to stamp the value across every filtered line.
    const [segmentUpdateOnlyRequired, setSegmentUpdateOnlyRequired] = useState(true);
    const [segmentPopulateError, setSegmentPopulateError] = useState<string | null>(null);
    const [isSegmentPopulating, setIsSegmentPopulating] = useState(false);

    // Keep the latest callback refs so effects don't need to re-run when the
    // parent passes a new function identity on each render (e.g. in tests or
    // when useCases() is called inside the component body).
    const searchMasterPropertyCodesRef = useRef(searchMasterPropertyCodes);
    searchMasterPropertyCodesRef.current = searchMasterPropertyCodes;
    const searchSegmentValuesRef = useRef(searchSegmentValues);
    searchSegmentValuesRef.current = searchSegmentValues;

    const handleAutoPopulateOpenChange = useCallback((open: boolean) => {
        setIsAutoPopulateOpen(open);
        if (!open) {
            setSelectedPropcodeOption(null);
            setAutoPopulateError(null);
        }
    }, []);

    const handleSegmentPopulateOpenChange = useCallback((open: boolean) => {
        setIsSegmentPopulateOpen(open);
        if (!open) {
            setSelectedSegmentType(1);
            setSegmentValue("");
            setSegmentValueOption(null);
            setSegmentUpdateOnlyRequired(true);
            setSegmentPopulateError(null);
        }
    }, []);

    /** Stable search callback passed to SearchableSelect for the chosen segment type. */
    const searchSegmentValuesForModal = useCallback(
        (segmentType: number, query: string) => searchSegmentValuesRef.current(segmentType, query),
        [],
    );

    /**
     * Stable search callback for the propcode SearchableSelect: delegates to the
     * server-side master property-code search so the query matches the full company
     * master set (code OR name), not just a client-loaded page.
     */
    const searchPropcodesForModal = useCallback(
        (query: string) => searchMasterPropertyCodesRef.current(query),
        [],
    );

    const handleAutoPopulatePropcodes = useCallback(async () => {
        if (!selectedPropcode) {
            setAutoPopulateError("Select a propcode before auto-populating.");
            return;
        }
        if (matchingRowCount === 0) {
            setAutoPopulateError("No line items match the current filter.");
            return;
        }
        setIsAutoPopulating(true);
        setAutoPopulateError(null);
        try {
            const updated = await onAutoPopulatePropcodesByFilter(
                activeFilterQuery,
                selectedPropcode,
            );
            handleAutoPopulateOpenChange(false);
            onSuccess?.(
                `Propcode auto-populated on ${updated} line item${updated === 1 ? "" : "s"}.`,
            );
            onRefresh();
        } catch (error) {
            setAutoPopulateError(
                error instanceof Error ? error.message : "Failed to auto-populate propcodes.",
            );
        } finally {
            setIsAutoPopulating(false);
        }
    }, [
        selectedPropcode,
        matchingRowCount,
        activeFilterQuery,
        onAutoPopulatePropcodesByFilter,
        handleAutoPopulateOpenChange,
        onRefresh,
        onSuccess,
    ]);

    const handleAutoPopulateSegmentsSubmit = useCallback(async () => {
        const isLookupSegment = [2, 4, 5, 6, 7].includes(selectedSegmentType);
        if (isLookupSegment && !segmentValue.trim()) {
            setSegmentPopulateError("Select a value for this segment.");
            return;
        }
        if (matchingRowCount === 0) {
            setSegmentPopulateError("No line items match the current filter.");
            return;
        }
        setIsSegmentPopulating(true);
        setSegmentPopulateError(null);
        try {
            const updated = await onAutoPopulateSegmentsByFilter(
                activeFilterQuery,
                selectedSegmentType,
                segmentValue.trim(),
                segmentUpdateOnlyRequired,
            );
            handleSegmentPopulateOpenChange(false);
            onSuccess?.(
                `Segment ${selectedSegmentType} auto-populated on ${updated} line item${
                    updated === 1 ? "" : "s"
                }.`,
            );
            onRefresh();
        } catch (error) {
            setSegmentPopulateError(
                error instanceof Error ? error.message : "Failed to auto-populate segments.",
            );
        } finally {
            setIsSegmentPopulating(false);
        }
    }, [
        selectedSegmentType,
        segmentValue,
        segmentUpdateOnlyRequired,
        matchingRowCount,
        activeFilterQuery,
        onAutoPopulateSegmentsByFilter,
        handleSegmentPopulateOpenChange,
        onRefresh,
        onSuccess,
    ]);

    // A propcode and a segment auto-populate must not overlap: while either is in
    // flight, both actions are blocked (mirrors the backend file lock, which fails
    // a concurrent auto-populate with a 409).
    const isAnyAutoPopulating = isAutoPopulating || isSegmentPopulating;

    return {
        isAnyAutoPopulating,
        isAutoPopulateOpen,
        setIsAutoPopulateOpen,
        handleAutoPopulateOpenChange,
        selectedPropcode,
        selectedPropcodeOption,
        setSelectedPropcodeOption,
        autoPopulateError,
        isAutoPopulating,
        handleAutoPopulatePropcodes,
        /** Stable search callback for the propcode SearchableSelect — server-side master property-code search. */
        searchPropcodesForModal,
        isSegmentPopulateOpen,
        handleSegmentPopulateOpenChange,
        selectedSegmentType,
        setSelectedSegmentType,
        segmentValue,
        setSegmentValue,
        segmentValueOption,
        setSegmentValueOption,
        segmentUpdateOnlyRequired,
        setSegmentUpdateOnlyRequired,
        segmentPopulateError,
        isSegmentPopulating,
        /** Stable search callback for the segment-value SearchableSelect — scoped to the chosen segment type. */
        searchSegmentValuesForModal,
        handleAutoPopulateSegmentsSubmit,
    };
}
